import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { generateTimeOptions, normalizeTime, normalizeTimeInput } from '../../utils/time';
import styles from './TimeDropdownInput.module.css';

const TimeDropdownInput = ({
  value,
  onChange,
  onBlur,
  onSelect,
  placeholder,
  label,
  options,
  disabled,
  normalizeInput,
  normalizeSelection,
  readOnly,
}) => {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const containerRef = useRef(null);
  const timeOptions = useMemo(() => options || generateTimeOptions(), [options]);
  const normalizeInputValue = normalizeInput || normalizeTimeInput;
  const normalizeSelectionValue = normalizeSelection || normalizeTime;

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
        if (onBlur) {
          const normalized = normalizeInputValue(value);
          onChange(normalized);
          onBlur(normalized);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [normalizeInputValue, onBlur, onChange, open, value]);

  const handleInputChange = (event) => {
    if (readOnly) return;
    onChange(event.target.value);
    setOpen(true);
  };

  const handleSelect = (option) => {
    const normalized = normalizeSelectionValue(option);
    onChange(normalized);
    if (onSelect) {
      onSelect(normalized);
    }
    setOpen(false);
  };

  const handleKeyDown = (event) => {
    if (!open && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      setOpen(true);
      setHighlighted(0);
      event.preventDefault();
      return;
    }

    if (event.key === 'Tab') {
      const normalized = normalizeInputValue(value);
      onChange(normalized);
      if (onBlur) {
        onBlur(normalized);
      }
    }

    if (event.key === 'ArrowDown') {
      setHighlighted((prev) => (prev + 1) % timeOptions.length);
      event.preventDefault();
    } else if (event.key === 'ArrowUp') {
      setHighlighted((prev) => (prev <= 0 ? timeOptions.length - 1 : prev - 1));
      event.preventDefault();
    } else if (event.key === 'Enter' && highlighted >= 0 && open) {
      handleSelect(timeOptions[highlighted]);
      event.preventDefault();
    } else if (event.key === 'Escape' && open) {
      setOpen(false);
      setHighlighted(-1);
      event.preventDefault();
    }
  };

  return (
    <div className={styles.fieldWrapper} ref={containerRef}>
      {label && <span>{label}</span>}
      <input
        className={styles.input}
        value={value}
        onChange={handleInputChange}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        onBlur={(event) => {
          if (onBlur) {
            const normalized = normalizeInputValue(event.target.value);
            onChange(normalized);
            onBlur(normalized);
          }
          setOpen(false);
          setHighlighted(-1);
        }}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
      />
      {open && (
        <div className={styles.dropdown} role="listbox">
          {timeOptions.map((option, index) => (
            <button
              type="button"
              key={option}
              className={[styles.option, highlighted === index ? styles.optionActive : '']
                .filter(Boolean)
                .join(' ')}
              onMouseEnter={() => setHighlighted(index)}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => handleSelect(option)}
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

TimeDropdownInput.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  onSelect: PropTypes.func,
  placeholder: PropTypes.string,
  label: PropTypes.node,
  options: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
  normalizeInput: PropTypes.func,
  normalizeSelection: PropTypes.func,
  readOnly: PropTypes.bool,
};

TimeDropdownInput.defaultProps = {
  onBlur: undefined,
  onSelect: undefined,
  placeholder: '08:00',
  label: null,
  options: null,
  disabled: false,
  normalizeInput: undefined,
  normalizeSelection: undefined,
  readOnly: false,
};

export default TimeDropdownInput;
